import { Injectable } from '@angular/core';
import { Observable, from, of, defer, timer } from 'rxjs';
import { catchError, switchMap, tap, takeWhile, map, expand } from 'rxjs/operators';
import { fetchAuthSession } from '@aws-amplify/auth';
import { CognitoUserService } from './cognito-user.service';
import { environment } from '@environment';

/**
 * Provides an abstraction for retrieving the current ID token.
 * Order of retrieval:
 * 1. AWS Amplify (modular v6) via fetchAuthSession
 * 2. Legacy localStorage / amazon-cognito-identity-js pathway (CognitoUserService)
 *
 * Returns a cold Observable that resolves once and completes.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private firstToken: string | null = null;

  constructor(private readonly legacyCognitoUserService: CognitoUserService) {}

  /**
   * Get the current ID token (JWT) or null if none.
   */
  public getIdToken(): Observable<string | null> {
    // Enforce legacy sliding expiry window (EXPIRE_HOURS) exactly as before via CognitoUserService.checkExpiry.
    // If expired, clear any cached token and short‑circuit to null so downstream logic treats session as invalid.
    try {
      const stillValid = this.legacyCognitoUserService.checkExpiry();
      if (!stillValid) {
        this.firstToken = null;
        return of(null);
      }
    } catch (e) {
      // If expiry check throws, fail safe to no token
      this.firstToken = null;
      return of(null);
    }
    // Consult cache first (fast path)
    if (this.firstToken) {
      return of(this.firstToken);
    }

    // Retry delays (ms). Precedence: environment.authConfig.authTokenRetryDelays (number[] of ms, all >= 0) if present and valid, otherwise DEFAULT_RETRY_DELAYS.
    // The last element in this array triggers a force refresh attempt for the token.
    // To configure, add `authTokenRetryDelays: number[]` to your environment.authConfig, e.g. [0, 100, 200, 400].
    //
    // Rationale for DEFAULT_RETRY_DELAYS:
    //   - The progression [0, 100, 150, 250, 400, 650] ms is a semi-exponential backoff, starting with no delay (0 ms) for the first attempt to minimize latency for healthy sessions.
    //   - Subsequent delays increase to reduce load and avoid hammering the backend in case of transient failures, but are capped to keep total wait time reasonable for the user.
    //   - The values are chosen to provide a balance between responsiveness and resilience: short initial retries for quick recovery, then longer waits to avoid excessive retries.
    //   - The last value (650 ms) is higher to allow for a final, more patient attempt, which also triggers a force refresh of the session.
    //   - This pattern is not strictly exponential to avoid overly long waits, but increases fast enough to mitigate repeated backend errors.
    //   - Adjust these values if you observe either excessive retrying or unacceptable delays in token acquisition.
    const DEFAULT_RETRY_DELAYS = [0, 100, 150, 250, 400, 650]; // final attempt uses forceRefresh
    const retryDelays = (environment as any)?.authConfig?.authTokenRetryDelays as number[] | undefined;
    const delays = this.resolveRetryDelays(retryDelays, DEFAULT_RETRY_DELAYS);
    const maxAttempts = delays.length;
    let attempt = 0;

    return defer(() => of(null)).pipe(
      expand(() => {
        if (attempt >= maxAttempts) return of(); // complete
        const delay = delays[attempt];
        const force = attempt === maxAttempts - 1; // only final attempt forces
        return timer(delay).pipe(
          switchMap(() => from(fetchAuthSession(force ? ({ forceRefresh: true } as any) : undefined))),
          map((session: any) => {
            const idToken = session?.tokens?.idToken?.toString() || null;
            if (idToken && !this.firstToken) {
              this.firstToken = idToken;
            }
            attempt++;
            return idToken;
          })
        );
      }),
      takeWhile((token, index) => token === null && index < maxAttempts - 1, true),
      switchMap((maybeToken) => {
        if (maybeToken) {
          return of(maybeToken);
        }
        if (environment.authConfig?.enableLegacyTokenFallback) {
          return this.getLegacyToken().pipe(
            tap((legacy) => {
              if (legacy && !this.firstToken) {
                this.firstToken = legacy;
              }
            })
          );
        }
        return of(null);
      }),
      catchError((err) => {
        if (environment.authConfig?.enableLegacyTokenFallback) {
          return this.getLegacyToken().pipe(
            tap((legacy) => {
              if (legacy && !this.firstToken) {
                this.firstToken = legacy;
              }
            })
          );
        }
        return of(null);
      })
    );
  }

  /** Allow external priming (e.g., after signIn) */
  public cacheToken(token: string | null, opts: { force?: boolean } = {}): void {
    if (!token) return;
    const replacing = !!this.firstToken && this.firstToken !== token;
    if (opts.force || !this.firstToken || replacing) {
      this.firstToken = token;
    }
  }

  /** Clear any cached token (use on logout) */
  public clearCachedToken(): void {
    this.firstToken = null;
  }

  private getLegacyToken(): Observable<string | null> {
    return this.legacyCognitoUserService.getIdToken();
  }

  /**
   * Helper to resolve retry delays from environment or fallback to default.
   */
  private resolveRetryDelays(envDelays: number[] | undefined, defaultDelays: number[]): number[] {
    if (Array.isArray(envDelays) && envDelays.length > 0 && envDelays.every((n) => Number.isFinite(n) && n >= 0)) {
      return envDelays;
    }
    return defaultDelays;
  }
}
